import React, { useState, useEffect, useRef, useCallback } from "react";
import Webcam from "react-webcam";
import { Modal, Input, Button, Message, Grid, Progress, Icon, Divider } from "semantic-ui-react";
import { toast } from "react-semantic-toasts";

import { useDispatch, useSelector } from "react-redux";
import { uploadDocuments, webCamUpload } from "../actions";

const dataURItoBlob = dataURI => {
    const binary = atob(dataURI.split(",")[1]);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
    }

    return new Blob([new Uint8Array(array)], { type: "image/jpeg" });
};

const WebcamUpload = () => {
    const [imageName, setImageName] = useState(`camera_image_${Date.now()}`);
    const [note, setNote] = useState("");
    const [progress, setProgress] = useState(0);
    const [image, setImage] = useState(null);
    const [devices, setDevices] = useState([]);
    const [activeDeviceId, setActiveDeviceId] = useState(null);
    const [facingMode, setFacingMode] = useState("environment");
    const dispatch = useDispatch();
    const { invoiceId, openWebCam, documents, uploadError } = useSelector(
        ({ invoiceDocument }) => invoiceDocument
    );
    const [documentCount] = useState(documents.length);
    const progressHandler = useRef(null);

    const webcamRef = React.useRef(null);

    const handleDevices = useCallback(mediaDevices => {
        const videoDevices = mediaDevices.filter(({ kind }) => kind === "videoinput");
        setDevices(videoDevices);
    }, []);

    useEffect(() => {
        navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }, [handleDevices]);

    const switchCamera = () => {
        if (devices.length < 2) return;
        if (activeDeviceId) {
            const currentIndex = devices.findIndex(d => d.deviceId === activeDeviceId);
            const nextIndex = (currentIndex + 1) % devices.length;
            setActiveDeviceId(devices[nextIndex].deviceId);
            setFacingMode(prev => (prev === "user" ? "environment" : "user"));
        } else {
            setActiveDeviceId(devices[0].deviceId);
            setFacingMode("user");
        }
    };

    const isFrontCamera = facingMode === "user";

    const videoConstraints = activeDeviceId
        ? { deviceId: { exact: activeDeviceId } }
        : { facingMode: "environment" };
    const handleUpload = () => {
        if (image && imageName && invoiceId) {
            dispatch(uploadDocuments(image, invoiceId, `${imageName}.jpeg`, note));
            progressHandler.current = setInterval(increaseProgress, 50);
        }
    };

    const increaseProgress = () => setProgress(prevProgress => Math.min(90, prevProgress + 1));

    useEffect(() => {
        if (documentCount !== documents.length) {
            setProgress(100);
            clearInterval(progressHandler.current);
            setTimeout(() => {
                dispatch(webCamUpload({ openWebCam: false }));
            }, 1500);
        }
    }, [documents]);

    useEffect(() => {
        if (uploadError) {
            toast({
                description: `Error occurred while uploading file.`,
                time: 3000,
                type: "error",
                icon: "warning sign",
                title: "Error",
            });

            clearInterval(progressHandler.current);
            setProgress(0);
        }
    }, [uploadError]);

    const capture = React.useCallback(() => {
        const imageSrc = dataURItoBlob(webcamRef.current.getScreenshot());
        setImage(imageSrc);
    }, [webcamRef]);

    const dismissModal = () => {
        if (progress > 0) return;
        dispatch(webCamUpload({ openWebCam: false }));
    };

    return (
        <Modal size="large" open={!!openWebCam} onClose={dismissModal}>
            <Modal.Header>{image ? "Edit photo" : "Take photo"}</Modal.Header>
            <Modal.Content>
                {!!image ? (
                    <>
                        <Icon name={"file image"} className={"margin-auto green"} size="massive" />
                        <Divider hidden></Divider>
                        <Grid>
                            <Grid.Column computer={16} mobile={16}>
                                <label>Name</label>
                                <Input
                                    fluid
                                    autoFocus
                                    size="large"
                                    value={imageName}
                                    error={!imageName}
                                    labelPosition="right"
                                    label={{ basic: true, content: ".jpeg" }}
                                    placeholder="Set a descriptive file name"
                                    onChange={e => setImageName(e.target.value)}
                                />
                                {!imageName && (
                                    <Message error content="File name can not be empty" />
                                )}
                            </Grid.Column>
                            <Grid.Column computer={16} mobile={16}>
                                <label>Note</label>
                                <Input
                                    fluid
                                    size="large"
                                    value={note}
                                    placeholder="Set a note"
                                    onChange={e => setNote(e.target.value)}
                                />
                            </Grid.Column>
                            {progress > 0 && (
                                <Grid.Column computer={16} mobile={16}>
                                    <Progress percent={progress} active autoSuccess>
                                        {progress === 100 ? "Upload success" : "Uploading ..."}
                                    </Progress>
                                </Grid.Column>
                            )}
                        </Grid>
                    </>
                ) : (
                    <>
                        <Webcam
                            mirrored={isFrontCamera}
                            audio={false}
                            ref={webcamRef}
                            className="webcam"
                            videoConstraints={videoConstraints}
                            screenshotFormat="image/png"
                        />
                        {devices.length > 1 && (
                            <Button
                                icon="sync"
                                circular
                                className="switch-camera-button"
                                onClick={switchCamera}
                                title="Switch Camera"
                            />
                        )}
                        <div className="photo-button" onClick={capture}>
                            <div id="circle"></div>
                            <div id="ring"></div>
                        </div>
                    </>
                )}
            </Modal.Content>

            <Modal.Actions>
                <Button negative content="Cancel" onClick={dismissModal} disabled={progress > 0} />
                {!!image && (
                    <Button
                        content="Retake Photo"
                        onClick={() => setImage(null)}
                        disabled={progress > 0}
                    />
                )}
                <Button
                    positive
                    content="Upload"
                    disabled={!imageName || !image || progress > 0}
                    onClick={handleUpload}
                />
            </Modal.Actions>
        </Modal>
    );
};

export default WebcamUpload;
